import { useRef, useState } from 'react';
import PortalIdParser from 'PortalIdParser';
import { getQuoteId, isImmutableQuote } from 'quotes-ui-lib/utils/quote';
import QuotesLogger from 'quotes-ui-lib/tracking/quotesLogger';
import { filterAndAddDividerBetweenGroups } from 'quotes-ui-lib/utils/uiDropdown';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
// Action Buttons
import ArchiveButton, { showArchiveButton } from 'quotes-ui-lib/components/ArchiveButton';
import CloneButton, { showCloneButton } from 'quotes-ui-lib/components/CloneButton';
import ConvertToInvoiceButton, { showConvertToInvoiceButton } from 'quotes-ui-lib/components/ConvertToInvoiceButton';
import CountersignButton, { shouldShowCountersignButton } from 'quotes-ui-lib/components/CountersignButton';
import CopyButton, { showCopyButton } from 'quotes-ui-lib/components/CopyButton';
import DeleteButton, { showDeleteButton } from 'quotes-ui-lib/components/DeleteButton';
import DetailsButton, { showDetailsButton } from 'quotes-ui-lib/components/DetailsButton';
import DownloadButton, { showDownloadButton } from 'quotes-ui-lib/components/DownloadButton';
import EditButton, { showEditButton } from 'quotes-ui-lib/components/EditButton';
import MarkSignedButton, { showMarkSignedButton } from 'quotes-ui-lib/components/MarkSignedButton';
import RecallAndEditButton, { showRecallAndEditButton } from 'quotes-ui-lib/components/RecallAndEditButton';
import SendButton, { showSendButton } from 'quotes-ui-lib/components/SendButton';
import ViewQuoteButton, { showViewQuoteButton } from 'quotes-ui-lib/components/ViewPublishedQuoteButton';
// Action Hooks
import useArchiveQuote from 'quotes-ui-lib/hooks/useArchiveQuote';
import useConvertToInvoice from 'quotes-ui-lib/hooks/useConvertToInvoice';
import useDeleteQuote from 'quotes-ui-lib/hooks/useDeleteQuote';
import useMarkSigned from 'quotes-ui-lib/hooks/useMarkSigned';
import useRecallAndEditQuote from 'quotes-ui-lib/hooks/useRecallAndEditQuote';
import useShouldCloseDropdownOnClick from 'quotes-ui-lib/hooks/useShouldCloseDropdownOnClick';
import useSendQuote from 'quotes-ui-lib/hooks/useSendQuote';

// Modals
import ConfirmArchiveModal from 'quotes-ui-lib/components/ConfirmArchiveModal';
import ConfirmConvertToInvoiceModal from 'quotes-ui-lib/components/ConfirmConvertToInvoiceModal';
import ConfirmMarkSignedModal from 'quotes-ui-lib/components/ConfirmMarkSignedModal';
import DeleteQuoteModal from 'quotes-ui-lib/components/DeleteQuoteModal';
import RecallPublishedQuoteModal from 'quotes-ui-lib/components/RecallPublishedQuoteModal';
import ConvertToSubscriptionButtonWrapper, { useIsConvertableToSubscription } from './ConvertToSubscriptionButton';
import ShareQuoteModal from 'quotes-ui-lib/components/ShareQuoteModal';
import { directToCPQQuoteEditor } from '../utils/cpqQuote';
import { isCPQQuote } from '../utils/templates';
import ActivityButton, { showActivityButton } from './ActivityButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ActionsDropdown = ({
  dropdownProps = {},
  quote,
  trackingLocation,
  fullQuoteUrl,
  userEmail,
  quoteSigners,
  isDetailsPage = false,
  isActivityPage = false,
  canCurrentUserEditQuote,
  scopes,
  userOwnerId,
  isScopedForInvoices,
  isScopedForBilling = false,
  showInvoiceEditor,
  showQuoteEditor,
  onQuotePropertyUpdate,
  onQuoteDelete,
  onQuoteClone,
  onQuoteRecall,
  onQuoteView,
  isUngatedForSeamlessSending,
  isUngatedForQuoteTracking,
  isUngatedForAiGenerateBuyerEmail,
  checkHasQuoteAssociations = false
}) => {
  const quoteId = String(getQuoteId(quote));
  const cloneButtonRef = useRef(null);
  const countersignButtonRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const shouldCloseDropdownOnClick = useShouldCloseDropdownOnClick([cloneButtonRef, countersignButtonRef, downloadButtonRef]);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const trackAction = action => QuotesLogger.log('quoteInteraction', {
    action,
    location: trackingLocation
  });
  const [isMarkSignedModalOpen, setMarkSignedModalOpen, isMarkSignedLoading, markSigned] = useMarkSigned(quoteId, trackingLocation, () => onQuotePropertyUpdate(quoteId, 'hs_manually_signed', 'true'));
  const {
    isModalOpen: isShareQuoteModalOpen,
    setIsModalOpen: setShareQuoteModalOpen,
    loadContacts,
    contacts,
    isLoading: isContactsLoading
  } = useSendQuote({
    quote: isImmutableQuote(quote) ? quote.toJS() : quote,
    checkHasQuoteAssociations
  });
  const handleMarkSignedClick = () => {
    trackAction('Open mark as manually signed modal');
    setMarkSignedModalOpen(true);
  };
  const handleOpenQuoteEditor = currentQuote => {
    if (isCPQQuote(currentQuote)) {
      directToCPQQuoteEditor(currentQuote);
    } else {
      showQuoteEditor(String(getQuoteId(currentQuote)));
    }
  };
  const [isRecallModalOpen, setRecallModalOpen, recallLoading, recallQuote] = useRecallAndEditQuote(quote, trackingLocation, recalledQuote => {
    handleOpenQuoteEditor(recalledQuote);
    if (onQuoteRecall) {
      onQuoteRecall(recalledQuote);
    }
  });
  const handleRecallClick = () => {
    trackAction('Open recall quote modal');
    setRecallModalOpen(true);
  };
  const [isArchiveModalOpen, setArchiveModalOpen, archiveLoading, archiveQuote] = useArchiveQuote(quoteId, trackingLocation, () => onQuotePropertyUpdate(quoteId, 'hs_archived', 'true'));
  const handleArchiveClick = () => {
    trackAction('Open archive quote modal');
    setArchiveModalOpen(true);
  };
  const [isDeleteModalOpen, setDeleteModalOpen, deleteLoading, deleteQuote] = useDeleteQuote(quote, trackingLocation, () => onQuoteDelete && onQuoteDelete(quoteId));
  const handleDeleteClick = () => {
    trackAction('Open delete quote modal');
    setDeleteModalOpen(true);
  };
  const [isConvertToInvoiceModalOpen, setIsConvertToInvoiceModalOpen, convertToInvoice] = useConvertToInvoice(() => showInvoiceEditor(quoteId), trackingLocation);
  const isSubscriptionConvertable = useIsConvertableToSubscription({
    quote,
    isActionsDropdownOpen: actionsDropdownOpen,
    isScopedForSubscription: isScopedForBilling,
    portalId: PortalIdParser.get()
  });
  const handleEditClick = () => {
    trackAction('Edit quote');
    handleOpenQuoteEditor(quote);
  };
  const handleViewQuoteClick = () => {
    trackAction('Click view quote');
    onQuoteView === null || onQuoteView === void 0 || onQuoteView(quote);
  };
  const isDetailsOrActivityPage = isDetailsPage || isActivityPage;
  const renderDetailsOrActivityButton = () => {
    if (isUngatedForQuoteTracking) {
      return !isDetailsOrActivityPage && showActivityButton(quote) && /*#__PURE__*/_jsx(ActivityButton, {
        quote: quote,
        trackingLocation: trackingLocation
      }, "activityButton");
    }
    return !isDetailsOrActivityPage && showDetailsButton(quote) && /*#__PURE__*/_jsx(DetailsButton, {
      quote: quote,
      trackingLocation: trackingLocation
    }, "detailsButton");
  };

  // Each group is separated by a divider
  const dropdownActionGroups = [[!isDetailsOrActivityPage && !!onQuoteView && showViewQuoteButton(quote) && /*#__PURE__*/_jsx(ViewQuoteButton, {
    handleClick: handleViewQuoteClick
  }, "viewQuoteButton"), showEditButton(quote, canCurrentUserEditQuote) && /*#__PURE__*/_jsx(EditButton, {
    onClick: handleEditClick
  }, "editButton"), renderDetailsOrActivityButton(), !isDetailsOrActivityPage && showCopyButton(quote, canCurrentUserEditQuote, fullQuoteUrl) && /*#__PURE__*/_jsx(CopyButton, {
    quote: quote,
    trackingLocation: trackingLocation,
    fullQuoteUrl: fullQuoteUrl
  }, "copyButton"), isUngatedForSeamlessSending && showSendButton(quote, userOwnerId) && /*#__PURE__*/_jsx(SendButton, {
    setShareQuoteModalOpen: setShareQuoteModalOpen,
    loadContacts: loadContacts,
    isContactsLoading: isContactsLoading,
    trackAction: trackAction
  }, "sendButton"), showDownloadButton(quote) && /*#__PURE__*/_jsx(DownloadButton, {
    quote: quote,
    trackingLocation: trackingLocation,
    buttonRef: downloadButtonRef,
    onLoadingFinish: () => setActionsDropdownOpen(false)
  }, "downloadButton")], [shouldShowCountersignButton(quote, userEmail, quoteSigners) && /*#__PURE__*/_jsx(CountersignButton, {
    buttonRef: countersignButtonRef,
    quote: quote,
    trackingLocation: trackingLocation,
    onLoadingFinish: () => setActionsDropdownOpen(false)
  }, "countersignButton"), showMarkSignedButton(quote, canCurrentUserEditQuote) && /*#__PURE__*/_jsx(MarkSignedButton, {
    quote: quote,
    onClick: handleMarkSignedClick
  }, "markSignedButton"), isSubscriptionConvertable ? /*#__PURE__*/_jsx(ConvertToSubscriptionButtonWrapper, {
    quote: quote,
    portalId: PortalIdParser.get()
  }, "convert-to-subscription-button") : showConvertToInvoiceButton(quote, isScopedForInvoices) && /*#__PURE__*/_jsx(ConvertToInvoiceButton, {
    quote: quote,
    portalId: PortalIdParser.get(),
    onClick: convertToInvoice,
    showModal: () => setIsConvertToInvoiceModalOpen(true)
  }, "convert-to-invoice-button")], [showCloneButton(quote, canCurrentUserEditQuote) && /*#__PURE__*/_jsx(CloneButton, {
    buttonRef: cloneButtonRef,
    quote: quote,
    trackingLocation: trackingLocation,
    onCloningSucceed: clonedQuote => {
      onQuoteClone === null || onQuoteClone === void 0 || onQuoteClone(clonedQuote);
      setActionsDropdownOpen(false);
      handleOpenQuoteEditor(clonedQuote);
    }
  }, "cloneButton"), showRecallAndEditButton(quote, canCurrentUserEditQuote) && /*#__PURE__*/_jsx(RecallAndEditButton, {
    quote: quote,
    trackingLocation: trackingLocation,
    onClick: handleRecallClick
  }, "recallAndEditButton"), showArchiveButton(quote, canCurrentUserEditQuote) && /*#__PURE__*/_jsx(ArchiveButton, {
    quote: quote,
    onClick: handleArchiveClick
  }, "archiveButton"), showDeleteButton(canCurrentUserEditQuote) && /*#__PURE__*/_jsx(DeleteButton, {
    quote: quote,
    onClick: handleDeleteClick
  }, "deleteButton")]];
  const dropdownListItems = filterAndAddDividerBetweenGroups(dropdownActionGroups);
  if (!dropdownListItems.length) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Dropdown, {
      buttonSize: dropdownProps.buttonSize,
      buttonUse: dropdownProps.buttonUse,
      placement: dropdownProps.placement,
      menuWidth: dropdownProps.menuWidth,
      "data-test-id": "quote-actions-dropdown",
      "data-selenium-test": "quote-actions-dropdown",
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.quoteActions.dropdown.buttonText"
      }),
      open: actionsDropdownOpen,
      onOpenChange: event => setActionsDropdownOpen(event.target.value),
      closeOnMenuClick: false,
      shouldCloseOnClick: shouldCloseDropdownOnClick,
      children: /*#__PURE__*/_jsx(List, {
        "data-test-id": "quote-actions-dropdown-list",
        wrapChildren: true,
        children: dropdownListItems
      })
    }), /*#__PURE__*/_jsx(RecallPublishedQuoteModal, {
      isOpen: isRecallModalOpen,
      onConfirm: recallQuote,
      onReject: () => setRecallModalOpen(false),
      loading: recallLoading
    }), /*#__PURE__*/_jsx(ConfirmArchiveModal, {
      quote: quote,
      isOpen: isArchiveModalOpen,
      loading: archiveLoading,
      onConfirm: archiveQuote,
      onReject: () => setArchiveModalOpen(false)
    }), /*#__PURE__*/_jsx(DeleteQuoteModal, {
      quote: quote,
      isOpen: isDeleteModalOpen,
      loading: deleteLoading,
      onConfirm: deleteQuote,
      onReject: () => setDeleteModalOpen(false)
    }), /*#__PURE__*/_jsx(ConfirmConvertToInvoiceModal, {
      isOpen: isConvertToInvoiceModalOpen,
      onConfirm: convertToInvoice,
      onReject: () => setIsConvertToInvoiceModalOpen(false)
    }), /*#__PURE__*/_jsx(ConfirmMarkSignedModal, {
      isOpen: isMarkSignedModalOpen,
      loading: isMarkSignedLoading,
      onConfirm: markSigned,
      onReject: () => setMarkSignedModalOpen(false)
    }), isShareQuoteModalOpen && /*#__PURE__*/_jsx(ShareQuoteModal, {
      scopes: scopes,
      onClose: () => {
        setShareQuoteModalOpen(false);
      },
      quote: quote,
      contacts: contacts,
      trackingLocation: trackingLocation,
      isUngatedForAiGenerateBuyerEmail: isUngatedForAiGenerateBuyerEmail
    })]
  });
};
export default ActionsDropdown;